import { lazy, Suspense, useState } from "react";
import { Link } from "react-router-dom";
import { Star, Expand } from "lucide-react";
import { Button } from "@/components/ui/button";
import { ProgressiveImage } from "@/components/ui/progressive-image";
import type { Experience } from "@/types/experience";

const ImageLightbox = lazy(() =>
  import("./ImageLightbox").then((m) => ({ default: m.ImageLightbox }))
);

interface ExperienceCardProps {
  experience: Experience;
  index?: number;
}

const placeholderImages = [
  "https://images.unsplash.com/photo-1544367567-0f2fcb009e0b?w=400&q=75",
];

const UNSPLASH_WIDTHS = [300, 400, 600, 800];

function getUnsplashSrcSet(url: string): string {
  return UNSPLASH_WIDTHS.map((w) => {
    const u = new URL(url);
    u.searchParams.set("w", String(w));
    u.searchParams.set("q", "75");
    u.searchParams.set("fm", "webp");
    return `${u.toString()} ${w}w`;
  }).join(", ");
}

function getSupabaseTransformUrl(url: string): string {
  return url.replace(
    "/storage/v1/object/public/",
    "/storage/v1/render/image/public/"
  ) + "?width=600&resize=contain";
}

function isUnsplashUrl(url: string): boolean {
  return url.includes("images.unsplash.com");
}

function isSupabaseStorageUrl(url: string): boolean {
  return url.includes("/storage/v1/object/public/");
}

export function ExperienceCard({ experience, index = 999 }: ExperienceCardProps) {
  const [lightboxOpen, setLightboxOpen] = useState(false);

  const images = experience.images.length > 0 ? experience.images : placeholderImages;
  const rawImageUrl = images[0];

  const isEager = index < 4;

  // Determine optimized image URL and srcSet
  let imageUrl = rawImageUrl;
  let srcSet: string | undefined;
  let sizes: string | undefined;

  if (isUnsplashUrl(rawImageUrl)) {
    srcSet = getUnsplashSrcSet(rawImageUrl);
    sizes = "(min-width: 1024px) 25vw, (min-width: 640px) 50vw, 100vw";
    // Use 600w webp as default src
    const u = new URL(rawImageUrl);
    u.searchParams.set("w", "600");
    u.searchParams.set("q", "75");
    u.searchParams.set("fm", "webp");
    imageUrl = u.toString();
  } else if (isSupabaseStorageUrl(rawImageUrl)) {
    imageUrl = getSupabaseTransformUrl(rawImageUrl);
  }

  // Use slug if available, otherwise fall back to ID
  const experienceUrl = `/experience/${experience.slug || experience.id}`;

  const handleImageClick = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setLightboxOpen(true);
  };

  return (
    <>
      <div className="group overflow-hidden rounded-xl bg-card transition-shadow hover:shadow-lg">
        {/* Image */}
        <div className="relative aspect-[4/3] overflow-hidden">
          <Link to={experienceUrl}>
            <ProgressiveImage
              src={imageUrl}
              srcSet={srcSet}
              sizes={sizes}
              alt={experience.title}
              loading={isEager ? "eager" : "lazy"}
              fetchPriority={isEager ? "high" : undefined}
              wrapperClassName="h-full w-full"
              className="h-full w-full object-cover transition-transform duration-300 group-hover:scale-105"
            />
          </Link>
          {/* Expand button for lightbox */}
          <button
            onClick={handleImageClick}
            className="absolute left-3 top-3 flex h-8 w-8 items-center justify-center rounded-full bg-background/90 text-foreground opacity-0 backdrop-blur-sm transition-opacity group-hover:opacity-100 hover:bg-background"
            title="View photos"
          >
            <Expand className="h-4 w-4" />
          </button>
          {/* Rating Badge */}
          <div className="absolute right-3 top-3 flex items-center gap-1 rounded-full bg-background/90 px-2 py-1 text-sm backdrop-blur-sm">
            <Star className="h-3.5 w-3.5 fill-star text-star" />
            <span className="font-medium">{experience.rating.toFixed(1)}</span>
            <span className="text-muted-foreground">({experience.review_count})</span>
          </div>
        </div>

      {/* Content */}
      <div className="p-4">
        <Link to={experienceUrl}>
          <h3 className="font-display text-lg font-semibold text-foreground hover:text-primary">
            {experience.title}
          </h3>
        </Link>
        <p className="mt-1 line-clamp-2 text-sm text-muted-foreground">
          {experience.description}
        </p>

        <div className="mt-4 flex items-center justify-between">
          <div>
            <span className="text-xs text-muted-foreground">FROM</span>
            <p className="font-display text-lg font-bold text-foreground">
              ₹{experience.price_per_person.toLocaleString()}
              <span className="text-sm font-normal text-muted-foreground">/person</span>
            </p>
          </div>
          <Button asChild size="sm" variant="outline">
            <Link to={experienceUrl}>Book</Link>
          </Button>
        </div>
      </div>
    </div>

      {lightboxOpen && (
        <Suspense fallback={null}>
          <ImageLightbox
            images={images}
            initialIndex={0}
            open={lightboxOpen}
            onOpenChange={setLightboxOpen}
            alt={experience.title}
          />
        </Suspense>
      )}
    </>
  );
}
